import { useEffect, useState } from 'react';
import { useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { getCompanyServices, PersonType } from '../../services/services';
import { Title } from '../../styles/global';
import { errorToast } from '../../utils/toast';
import { Content } from '../BodyStep/styles';
import FlowButton from '../FlowButton';
import Loading from '../Loading';
import ToggleSwitch from '../ToggleSwitch';
import { ServiceRow, ServicesList } from './styles';

interface Service {
  cos_ser_id: number;
  ser_name: string;
  ser_description: string;
  ser_pubsub: string;
  ser_additional_fields: boolean;
}

export default function ServicesSelection() {
  // Tipo de pessoa cuja lista já está carregada. A troca entre "Consultas
  // Pessoa Física" e "Pessoa Jurídica" pelo cabeçalho mantém esta etapa na
  // tela — o componente não remonta —, então a lista precisa ser recarregada
  // quando o tipo muda. Antes ela era buscada uma vez só e, no PJ, continuava
  // mostrando (e enviando) os serviços do PF.
  const [loadedType, setLoadedType] = useState<PersonType | null>(null);
  const {
    selectedServices,
    setSelectedServices,
    handleChangeCurrentStep,
    personType,
  } = useSteps();
  const { translationPage } = useAppTranslation();

  const { tp } = translationPage(tra => tra.components.servicesSelection);

  const [services, setServices] = useState<(Service & { selected: boolean })[]>(
    [],
  );

  const toggle = (id: number, value: boolean) => {
    setServices(prev => {
      const copy = [...prev];
      const index = prev.findIndex(s => s.cos_ser_id === id);
      const found = { ...copy[index] };
      found.selected = value;
      copy[index] = found;
      return copy;
    });
  };

  const isMounted = loadedType === personType;

  useEffect(() => {
    if (loadedType === personType) return undefined;

    const controller = new AbortController();
    // Lista do tipo anterior sai da tela na hora: enquanto a nova carrega,
    // não pode sobrar serviço do outro tipo para ser marcado e enviado.
    setServices([]);

    const getServices = async (signal: AbortSignal) => {
      const data = await getCompanyServices(signal, personType);
      if (signal.aborted) return;

      const selectedServicesIds = selectedServices.map(s => s.cos_ser_id);

      setServices(
        data.map(s => ({
          ...s,
          selected:
            selectedServices.length === 0 ||
            selectedServicesIds.includes(s.cos_ser_id),
        })),
      );
      setLoadedType(personType);
    };

    getServices(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadedType, selectedServices, personType]);

  const nextStep = () => {
    const localSelectedServices = services.filter(s => s.selected);

    if (localSelectedServices.length === 0) {
      errorToast({ message: tp('selectAtLeastOne') });
      return;
    }

    if (localSelectedServices.length === services.length) {
      setSelectedServices([]);
    }

    if (localSelectedServices.length !== services.length) {
      setSelectedServices(localSelectedServices);
    }

    handleChangeCurrentStep();
  };

  return (
    <Content>
      <Title>{tp('title')}</Title>
      <ServicesList>
        {!isMounted && <Loading />}
        {services.map(s => (
          <ServiceRow key={s.cos_ser_id}>
            <ToggleSwitch
              id={`toggle-${s.cos_ser_id}`}
              checked={s.selected}
              title={s.ser_description}
              onChange={value => toggle(s.cos_ser_id, value)}
            />
          </ServiceRow>
        ))}
      </ServicesList>
      <FlowButton text={tp('next')} type="button" onClick={nextStep} />
    </Content>
  );
}
